import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { CreateAgentIcon } from 'components/blocks/layout/icons/create-agent-icon';

import { SidebarCollapsibleSection } from 'components/blocks/layout/sidebar-section';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useSidebar } from 'components/ui/sidebar';
import {
  SIDEBAR_NAV_ICON_SIZE,
  SIDEBAR_NAV_ICON_STROKE,
  sidebarNavIconClass,
} from 'components/blocks/layout/sidebar-nav-icon';
import { isNestedSeeMoreDesign } from 'config/sidebar-see-more';
import { AgentSummary } from '../agent-chat-module/types/agent.service.type';
import { AgentChatAccordion } from './agent-chat-accordion';

const MAX_VISIBLE_AGENTS = 5;
const MAX_OVERFLOW_AGENTS = 10;

type AgentsSectionProps = {
  agents?: AgentSummary[];
  chatId?: string;
  variant?: 'default' | 'figma';
  renderRightAction?: (id: string, title: string) => ReactNode;
};

type AgentsListProps = {
  agents: AgentSummary[];
  chatId?: string;
  variant: 'default' | 'figma';
  renderRightAction?: (id: string, title: string) => ReactNode;
};

export function AgentsSection({
  agents = [],
  chatId,
  variant = 'figma',
  renderRightAction,
}: AgentsSectionProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const isAgentRoute =
    pathname.startsWith('/agentChat') || pathname.startsWith('/agents');

  if (agents.length === 0) {
    return null;
  }

  return (
    <SidebarCollapsibleSection
      title={t('AGENTS', 'Agents')}
      variant={variant}
      defaultOpen={isAgentRoute}
      onAddClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (isMobile) {
          setOpenMobile(false);
        }
        navigate('/agents?create=true');
      }}
    >
      <AgentsList
        agents={agents}
        chatId={chatId}
        variant={variant}
        renderRightAction={renderRightAction}
      />
    </SidebarCollapsibleSection>
  );
}

function AgentsList({
  agents,
  chatId,
  variant,
  renderRightAction,
}: AgentsListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isMobile, setOpenMobile } = useSidebar();
  const activeAgentIdFromUrl = searchParams.get('agent') || searchParams.get('agentId');

  const [openAgents, setOpenAgents] = useState<string[]>(() =>
    activeAgentIdFromUrl ? [activeAgentIdFromUrl] : [],
  );
  const [recentlyAccessedAgents, setRecentlyAccessedAgents] = useState<string[]>(() =>
    activeAgentIdFromUrl ? [activeAgentIdFromUrl] : [],
  );
  const [showAllAgents, setShowAllAgents] = useState(false);

  useEffect(() => {
    if (activeAgentIdFromUrl) {
      setOpenAgents((prev) =>
        prev.includes(activeAgentIdFromUrl) ? prev : [...prev, activeAgentIdFromUrl],
      );
    }
  }, [activeAgentIdFromUrl]);

  useEffect(() => {
    if (agents.length === 0) return;

    const validAgentIds = new Set(agents.map((agent) => agent.id));
    setRecentlyAccessedAgents((prev) => {
      const kept = prev.filter((id) => validAgentIds.has(id));
      const next =
        activeAgentIdFromUrl && validAgentIds.has(activeAgentIdFromUrl) && kept[0] !== activeAgentIdFromUrl
          ? [activeAgentIdFromUrl, ...kept.filter((id) => id !== activeAgentIdFromUrl)].slice(
              0,
              MAX_VISIBLE_AGENTS,
            )
          : kept;
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) return prev;
      return next;
    });
    setOpenAgents((prev) => {
      const next = prev.filter((id) => validAgentIds.has(id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) return prev;
      return next;
    });
  }, [agents, activeAgentIdFromUrl]);

  const handleExpandAgent = useCallback((agentId: string) => {
    setOpenAgents((prev) => (prev.includes(agentId) ? prev : [...prev, agentId]));
  }, []);

  const handleAgentClickFromDropdown = (agentId: string) => {
    setRecentlyAccessedAgents((prev) =>
      [agentId, ...prev.filter((id) => id !== agentId)].slice(0, MAX_VISIBLE_AGENTS),
    );
    handleExpandAgent(agentId);
  };

  const handleViewMoreAgents = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
    navigate('/agents');
  };

  const recentlyAccessed = recentlyAccessedAgents
    .map((id) => agents.find((agent) => agent.id === id))
    .filter((agent): agent is AgentSummary => Boolean(agent));
  const otherAgents = agents.filter((agent) => !recentlyAccessedAgents.includes(agent.id));
  const orderedAgents = [...recentlyAccessed, ...otherAgents];
  const hiddenAgents = orderedAgents.slice(MAX_VISIBLE_AGENTS);
  const overflowAgents = hiddenAgents.slice(0, MAX_OVERFLOW_AGENTS);
  const hasMoreThanOverflow = hiddenAgents.length > MAX_OVERFLOW_AGENTS;
  const displayedAgents =
    isNestedSeeMoreDesign && showAllAgents
      ? orderedAgents
      : orderedAgents.slice(0, MAX_VISIBLE_AGENTS);

  return (
    <div className="flex flex-col gap-1">
      {displayedAgents.map((agent) => (
        <AgentChatAccordion
          key={agent.id}
          agent={agent}
          chatId={chatId}
          variant={variant}
          isOpen={variant === 'figma' ? openAgents.includes(agent.id) : undefined}
          onActiveChatFound={handleExpandAgent}
          renderRightAction={renderRightAction}
        />
      ))}

      {isNestedSeeMoreDesign
        ? !showAllAgents && hiddenAgents.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAllAgents(true)}
              className="flex min-h-7 w-full items-center text-left group-data-[collapsible=icon]:hidden"
            >
              <span className="text-xs font-medium leading-[1.2] tracking-normal text-[#888888] transition-colors hover:text-[#3D3D3D] dark:text-grey-400 dark:hover:text-grey-200">
                {t('SEE_MORE')} ({hiddenAgents.length})
              </span>
            </button>
          )
        : hiddenAgents.length > 0 && (
            <div className="flex items-center justify-between w-full py-1 rounded-[8px] text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 w-full text-left group-data-[collapsible=icon]:justify-center">
                      <div className="flex items-center justify-center w-6 h-6" />
                      <span className="text-[13px] font-normal leading-normal group-data-[collapsible=icon]:hidden">
                        {t('SEE_MORE')} ({hiddenAgents.length})
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side={isMobile ? 'top' : 'right'}
                    align={isMobile ? 'end' : 'start'}
                    className={`${isMobile ? 'w-64 max-w-[calc(100vw-2rem)]' : 'w-48'} max-h-80 overflow-y-auto`}
                    sideOffset={isMobile ? 8 : 4}
                    alignOffset={isMobile ? -8 : 0}
                  >
                    {overflowAgents.map((agent) => (
                      <DropdownMenuItem
                        key={agent.id}
                        onClick={() => handleAgentClickFromDropdown(agent.id)}
                        className="flex items-center gap-2"
                      >
                        <CreateAgentIcon
                          width={SIDEBAR_NAV_ICON_SIZE}
                          height={SIDEBAR_NAV_ICON_SIZE}
                          strokeWidth={SIDEBAR_NAV_ICON_STROKE}
                          className={sidebarNavIconClass}
                        />
                        <span className="truncate">{agent.name || 'Unnamed Agent'}</span>
                      </DropdownMenuItem>
                    ))}
                    {hasMoreThanOverflow && (
                      <DropdownMenuItem onClick={handleViewMoreAgents} className="flex items-center gap-2">
                        <CreateAgentIcon
                          width={SIDEBAR_NAV_ICON_SIZE}
                          height={SIDEBAR_NAV_ICON_SIZE}
                          strokeWidth={SIDEBAR_NAV_ICON_STROKE}
                          className={`${sidebarNavIconClass} opacity-0`}
                          aria-hidden
                        />
                        <span className="truncate">{t('VIEW_MORE', 'View more')}</span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
    </div>
  );
}
